import React, { useEffect, useRef, useState } from 'react';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import styles from './RetakeControls.module.css';

function localDateTimeValue(date) {
  if (!Number.isFinite(date.getTime())) return '';
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

const MIN_RETAKE_WINDOW_MS = 60 * 60 * 1000;
const MAX_RETAKE_WINDOW_MS = 60 * 24 * 60 * 60 * 1000;

export function RetakeBadge({ count = 0, retake = null, compact = false }) {
  const startDate = retake?.startDate || retake?.assignedAt;
  const endDate = retake?.endDate || retake?.deadline;
  const statusText = retake?.status === 'pending'
    ? retake.availability === 'expired'
      ? 'Retake window closed'
      : retake.availability === 'upcoming'
      ? `Retake opens${startDate ? ` ${new Date(startDate).toLocaleDateString()}` : ''}`
      : `Retake available${endDate ? ` · until ${new Date(endDate).toLocaleDateString()}` : ''}`
    : retake?.status === 'in_progress'
      ? 'Retake in progress'
      : retake?.status === 'grading'
        ? 'Retake grading'
        : null;
  if (!count && !statusText) return null;
  return (
    <span className={styles.badges}>
      {count > 0 && <span className={styles.count}>Retaken ×{count}</span>}
      {statusText && <span className={styles.state} data-status={retake.status}>{compact ? retake.status.replace('_', ' ') : statusText}</span>}
    </span>
  );
}

export function RetakeHistoryList({ history = [] }) {
  if (!history.length) return <p className={styles.emptyHistory}>No completed retakes.</p>;
  return (
    <ol className={styles.history}>
      {history.map(item => (
        <li key={`${item.retakeNumber}:${item.submittedAt || item.assignedAt || item.supersededAt}`}>
          <span>Retake {item.retakeNumber}</span>
          <strong>{item.percentage == null ? '—' : `${item.percentage}%`}</strong>
          <small>{item.submittedAt ? new Date(item.submittedAt).toLocaleDateString() : 'Result archived'}</small>
          {item.reason && <small>{item.reason}</small>}
        </li>
      ))}
    </ol>
  );
}

export function RetakeAssignmentModal({ exam, selectedStudents, actor, fetchFn, renderQuestionEditor, dateTimePicker: DateTimePickerComponent, classes: ui = {}, reportCardReleased = false, onClose, onComplete }) {
  const [initialWindow] = useState(() => {
    const now = new Date();
    const start = new Date(now);
    start.setSeconds(0, 0);
    const hasSubMinute = now.getSeconds() > 0 || now.getMilliseconds() > 0;
    start.setMinutes(Math.ceil((now.getMinutes() + (hasSubMinute ? 1 : 0)) / 5) * 5);
    return {
      minStart: start,
      maxEnd: new Date(now.getTime() + MAX_RETAKE_WINDOW_MS),
      startDate: localDateTimeValue(start),
      endDate: localDateTimeValue(new Date(start.getTime() + 7 * 24 * 60 * 60 * 1000)),
    };
  });
  const [startDate, setStartDate] = useState(initialWindow.startDate);
  const [endDate, setEndDate] = useState(initialWindow.endDate);
  const [reason, setReason] = useState('');
  const [sourceExam, setSourceExam] = useState(exam);
  const [questionSetMode, setQuestionSetMode] = useState('generated');
  const [curriculumTopics, setCurriculumTopics] = useState([]);
  const [topics, setTopics] = useState([]);
  const [topicSubjectMap, setTopicSubjectMap] = useState({});
  const [topicCoverage, setTopicCoverage] = useState({});
  const [topicDraft, setTopicDraft] = useState('');
  const [teacherMultiSelect, setTeacherMultiSelect] = useState(true);
  const [showAllTopics, setShowAllTopics] = useState(false);
  const [questionType, setQuestionType] = useState('mcq');
  const [difficulty, setDifficulty] = useState('medium');
  const [countPerTopic, setCountPerTopic] = useState(3);
  const [questions, setQuestions] = useState([]);
  const [equalMarks, setEqualMarks] = useState(true);
  const [totalScore, setTotalScore] = useState('');
  const [loadingSource, setLoadingSource] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [uploadQModal, setUploadQModal] = useState(false);
  const [uploadQLoading, setUploadQLoading] = useState(false);
  const [uploadQError, setUploadQError] = useState('');
  const [uploadQType, setUploadQType] = useState('mcq');
  const [uploadQFileName, setUploadQFileName] = useState('');
  const uploadQFileRef = useRef(null);
  const uploadQFileDataRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const prefix = actor === 'teacher' ? '/teacher' : '/school';
    fetchFn(`${prefix}/exams/${exam._id}`).then(async response => {
      if (!cancelled && response.ok && response.data?.exam) {
        const loadedExam = response.data.exam;
        const loadedQuestions = loadedExam.questions || [];
        const classId = String(loadedExam.classId?._id || loadedExam.classId || '');
        const subjectRefs = loadedExam.isMultiSubject && loadedExam.subjects?.length
          ? loadedExam.subjects.map((subject, index) => ({
            subjectId: String(subject.subjectId?._id || subject.subjectId || ''),
            subjectName: subject.subjectName || '',
            subjectSectionIdx: index,
          }))
          : [{
            subjectId: String(loadedExam.subjectId?._id || loadedExam.subjectId || ''),
            subjectName: loadedExam.subjectName || '',
            subjectSectionIdx: null,
          }];
        const loadedCurriculumTopics = [];
        const nextTopicSubjectMap = {};
        const nextTopicCoverage = {};
        if (classId) {
          await Promise.all(subjectRefs.filter(subject => subject.subjectId).map(async subject => {
            const curriculumResponse = await fetchFn(`${prefix}/curriculum/coverage/${subject.subjectId}/${classId}`);
            for (const item of curriculumResponse.data?.coverage?.topics || []) {
              const name = String(item.name || '').trim();
              if (!name) continue;
              loadedCurriculumTopics.push(name);
              if (!nextTopicSubjectMap[name]) nextTopicSubjectMap[name] = subject;
              if (!nextTopicCoverage[name]) nextTopicCoverage[name] = item;
            }
          }));
        }
        if (cancelled) return;
        const originalTopics = [...new Set([
          ...(loadedExam.topics || []),
          ...loadedQuestions.map(question => question.topic).filter(Boolean),
        ].map(topic => String(topic).trim()).filter(Boolean))];
        setSourceExam(loadedExam);
        setCurriculumTopics([...new Set(loadedCurriculumTopics)]);
        setTopics(originalTopics);
        setTopicSubjectMap(nextTopicSubjectMap);
        setTopicCoverage(nextTopicCoverage);
        setTopicDraft(originalTopics[0] || '');
        setTeacherMultiSelect(true);
        setEqualMarks(loadedExam.settings?.equalMarks !== false);
        setTotalScore(String(loadedExam.settings?.totalScore || loadedQuestions.reduce((sum, question) => sum + (Number(question.score) || 0), 0) || ''));
      } else if (!cancelled) {
        setError(response.data?.error || 'Could not load the exam questions and topics.');
      }
      if (!cancelled) setLoadingSource(false);
    }).catch(() => {
      if (!cancelled) {
        setError('Could not load the exam questions and topics.');
        setLoadingSource(false);
      }
    });
    return () => { cancelled = true; };
  }, [actor, exam._id, fetchFn]);

  const originalTopics = [...new Set([
    ...(sourceExam?.topics || []),
    ...(sourceExam?.questions || []).map(question => question.topic).filter(Boolean),
  ].map(topic => String(topic).trim()).filter(Boolean))];
  const topicOptions = [...new Set([...curriculumTopics, ...originalTopics])];
  const selectedTopics = actor === 'teacher' && !teacherMultiSelect
    ? (topicDraft.trim() ? [topicDraft.trim()] : [])
    : topics;
  const fieldClass = actor === 'admin' ? ui.examFR : ui.formRow;
  const errorClass = actor === 'teacher' ? ui.aiError : ui.inlineError;
  const successClass = actor === 'teacher' ? ui.aiSuccess : ui.inlineSuccess;
  const helperClass = ui.hintText;
  const toggleSwitchClass = actor === 'teacher' ? ui.toggleSwitch : ui.toggleSwitchSm;
  const toggleSliderClass = actor === 'teacher' ? ui.toggleSlider : ui.toggleSliderSm;

  const toggleTopic = topic => setTopics(current => current.includes(topic)
    ? current.filter(item => item !== topic)
    : [...current, topic]);

  const selectTeacherTopic = topic => {
    if (teacherMultiSelect) toggleTopic(topic);
    else {
      setTopicDraft(topic);
      setTopics([topic]);
    }
  };

  const setTeacherSelectionMode = multiSelect => {
    if (multiSelect) {
      const singleTopic = topicDraft.trim();
      if (singleTopic && !topics.length) setTopics([singleTopic]);
      setCountPerTopic(current => Math.min(5, Number(current) || 1));
    } else {
      const firstTopic = topics[0] || '';
      setTopics(firstTopic ? [firstTopic] : []);
      setTopicDraft(firstTopic);
      setCountPerTopic(current => [1, 2, 3, 5, 10].includes(Number(current)) ? Number(current) : 3);
    }
    setTeacherMultiSelect(multiSelect);
  };

  const updateTeacherSingleTopic = value => {
    setTopicDraft(value);
    setTopics(value.trim() ? [value.trim()] : []);
  };

  const minEndDate = new Date(new Date(startDate).getTime() + MIN_RETAKE_WINDOW_MS);
  const updateStartDate = event => {
    const nextStartDate = event.target.value;
    const nextStartTime = new Date(nextStartDate).getTime();
    setStartDate(nextStartDate);
    if (Number.isFinite(nextStartTime) && new Date(endDate).getTime() < nextStartTime + MIN_RETAKE_WINDOW_MS) {
      setEndDate(localDateTimeValue(new Date(nextStartTime + MIN_RETAKE_WINDOW_MS)));
    }
  };

  const updateQuestion = (index, field, value) => setQuestions(current => current.map((question, questionIndex) => {
    if (questionIndex !== index) return question;
    if (field === '__replace__') return { ...value, question_number: index + 1 };
    return { ...question, [field]: value };
  }));

  const removeQuestion = index => setQuestions(current => current
    .filter((_, questionIndex) => questionIndex !== index)
    .map((question, questionIndex) => ({ ...question, question_number: questionIndex + 1 })));

  const addBlankQuestion = () => setQuestions(current => [...current, {
    question_number: current.length + 1,
    instructions: '',
    diagram: '',
    question_text: '',
    options: ['', '', '', ''],
    correct_answer: '',
    questionType: 'mcq',
    marking_guide: '',
    model_answer: '',
    topic: selectedTopics[0] || '',
    difficulty,
    score: 1,
    subjectSectionIdx: topicSubjectMap[selectedTopics[0]]?.subjectSectionIdx ?? null,
  }]);

  const openUploadQuestions = () => {
    setError('');
    setUploadQError('');
    setUploadQFileName('');
    uploadQFileDataRef.current = null;
    setUploadQModal(true);
  };

  const closeUploadQuestions = () => {
    setUploadQModal(false);
    setUploadQError('');
    setUploadQFileName('');
    uploadQFileDataRef.current = null;
  };

  const handleUploadQuestions = async () => {
    const file = uploadQFileDataRef.current;
    if (!file) {
      setUploadQError('Please select a file first.');
      return;
    }
    setUploadQLoading(true);
    setUploadQError('');
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('questionType', uploadQType);
      const prefix = actor === 'teacher' ? '/teacher' : '/school';
      const response = await fetchFn(`${prefix}/exams/${exam._id}/upload-questions`, {
        method: 'POST',
        body: formData,
      });
      if (!response.ok || !Array.isArray(response.data?.questions)) {
        setUploadQError(response.data?.error || 'Upload failed. Please try again.');
        return;
      }

      const importedQuestions = response.data.questions.map(question => {
        const topic = String(question.topic || '').trim() || selectedTopics[0] || '';
        return {
          instructions: '',
          diagram: '',
          options: [],
          correct_answer: '',
          questionType: uploadQType,
          marking_guide: '',
          model_answer: '',
          score: 1,
          ...question,
          topic,
          subjectSectionIdx: question.subjectSectionIdx
            ?? topicSubjectMap[topic]?.subjectSectionIdx
            ?? topicSubjectMap[selectedTopics[0]]?.subjectSectionIdx
            ?? null,
        };
      });
      setQuestions(current => [...current, ...importedQuestions]
        .map((question, index) => ({ ...question, question_number: index + 1 })));
      setQuestionSetMode('generated');
      closeUploadQuestions();
    } catch {
      setUploadQError('Connection error. Please try again.');
    } finally {
      setUploadQLoading(false);
    }
  };

  const generateQuestions = async () => {
    if (!selectedTopics.length) {
      setError('Add or select at least one topic before generating questions.');
      return;
    }
    const totalRequested = selectedTopics.length * Number(countPerTopic);
    if (totalRequested > 100) {
      setError('Generate at most 100 questions per retake. Reduce the topics or count per topic.');
      return;
    }
    setGenerating(true);
    setError('');
    try {
      const prefix = actor === 'teacher' ? '/teacher' : '/school';
      const generated = [];
      const mixedQuestionTypes = [];
      if (questionType === 'mixed') {
        for (let questionIndex = 0; questionIndex < Number(countPerTopic); questionIndex += 1) {
          mixedQuestionTypes.push(questionIndex % 2 === 0 ? 'mcq' : 'theory');
        }
      }
      const requestTypes = questionType === 'mixed' ? ['mcq', 'theory'] : [questionType];
      for (const topic of selectedTopics) {
        const topicSourceQuestion = (sourceExam?.questions || []).find(question => question.topic === topic);
        const topicSubject = topicSubjectMap[topic];
        const subjectSectionIdx = topicSourceQuestion?.subjectSectionIdx ?? topicSubject?.subjectSectionIdx ?? null;
        const topicSubjectName = subjectSectionIdx != null
          ? sourceExam?.subjects?.[subjectSectionIdx]?.subjectName
          : (topicSubject?.subjectName || sourceExam?.subjectName);
        const questionsByType = {};
        for (const generatedQuestionType of requestTypes) {
          const requestedCount = questionType === 'mixed'
            ? mixedQuestionTypes.filter(type => type === generatedQuestionType).length
            : Number(countPerTopic);
          if (!requestedCount) continue;
          const response = await fetchFn(`${prefix}/exams/${exam._id}/ai-suggest`, {
            method: 'POST',
            body: JSON.stringify({
              topic,
              count: requestedCount,
              difficulty,
              questionType: generatedQuestionType,
              subjectName: topicSubjectName || sourceExam?.subjectName || '',
            }),
          });
          if (!response.ok) throw new Error(response.data?.error || `Could not generate questions for "${topic}".`);
          questionsByType[generatedQuestionType] = (response.data?.questions || []).map(question => ({
            instructions: '',
            diagram: '',
            options: [],
            correct_answer: '',
            questionType: generatedQuestionType,
            marking_guide: '',
            model_answer: '',
            score: 1,
            ...question,
            subjectSectionIdx,
          }));
        }
        if (questionType === 'mixed') {
          const usedCounts = { mcq: 0, theory: 0 };
          for (const generatedQuestionType of mixedQuestionTypes) {
            const question = questionsByType[generatedQuestionType]?.[usedCounts[generatedQuestionType]];
            usedCounts[generatedQuestionType] += 1;
            if (question) generated.push(question);
          }
        } else {
          generated.push(...(questionsByType[questionType] || []));
        }
      }
      setQuestions(generated.map((question, index) => ({ ...question, question_number: index + 1 })));
      setQuestionSetMode('generated');
      if (equalMarks) {
        const oldTotal = Number(totalScore) || (sourceExam?.questions || []).reduce((sum, question) => sum + (Number(question.score) || 0), 0);
        if (oldTotal > 0) setTotalScore(String(oldTotal));
      }
    } catch (generationError) {
      setError(generationError.message || 'Question generation failed. You can still choose to reuse the original questions.');
    } finally {
      setGenerating(false);
    }
  };

  const normalizedQuestions = equalMarks && questions.length > 0
    ? questions.map((question, index) => ({
      ...question,
      question_number: index + 1,
      score: (Number(totalScore) || 0) / questions.length,
    }))
    : questions.map((question, index) => ({
      ...question,
      question_number: index + 1,
      score: Number(question.score) || 0,
    }));
  const effectiveTotalScore = questionSetMode === 'generated'
    ? (equalMarks ? Number(totalScore) : normalizedQuestions.reduce((sum, question) => sum + question.score, 0))
    : null;

  const submit = async () => {
    if (!reason.trim()) {
      setError('A reason is required.');
      return;
    }
    if (reason.trim().length < 5) {
      setError('Enter a reason with at least 5 characters.');
      return;
    }
    const endTime = new Date(endDate).getTime();
    const nowTime = Date.now();
    // The default start is fixed when the modal opens, so it can pass while questions are being prepared.
    const startTime = Math.max(new Date(startDate).getTime(), nowTime);
    if (!Number.isFinite(startTime) || !Number.isFinite(endTime)
      || endTime - startTime < MIN_RETAKE_WINDOW_MS
      || endTime - nowTime < MIN_RETAKE_WINDOW_MS
      || endTime - nowTime > MAX_RETAKE_WINDOW_MS) {
      setError('Choose a valid start and end date. The window must last at least 1 hour and end within 60 days.');
      return;
    }
    if (questionSetMode === 'generated' && (!questions.length || !Number.isFinite(effectiveTotalScore) || effectiveTotalScore <= 0)) {
      setError('Generate at least one question and confirm a positive total score, or choose to reuse the original questions.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const prefix = actor === 'teacher' ? '/teacher' : '/school';
      const response = await fetchFn(`${prefix}/exams/${exam._id}/retakes`, {
        method: 'POST',
        body: JSON.stringify({
          membershipIds: selectedStudents.map(student => String(student.membershipId)),
          reason: reason.trim(),
          startDate: new Date(startTime).toISOString(),
          endDate: new Date(endDate).toISOString(),
          questionSetMode,
          ...(questionSetMode === 'generated' ? {
            questions: normalizedQuestions,
            topics: selectedTopics,
            equalMarks,
            totalScore: effectiveTotalScore,
          } : { topics: selectedTopics }),
        }),
      });
      if (!response.ok) {
        setError(response.data?.error || 'Unable to assign retakes.');
        return;
      }
      setResult(response.data);
      onComplete?.(response.data);
    } catch {
      setError('Connection error. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const coverageNoticeTopics = selectedTopics.filter(topic => topicCoverage[topic]?.covered);
  const renderCoverageChip = (topic, showSelectionMarker = false) => {
    const coverage = topicCoverage[topic];
    return (
      <span className={`${ui.coverageTopicChip} ${coverage?.covered ? ui.coverageTopicChipCovered : ui.coverageTopicChipUncovered}`}>
        {showSelectionMarker ? '✓ ' : ''}{coverage ? (coverage.covered ? '✓' : '○') : loadingSource ? '…' : '○'} {topic}
        {coverage?.covered && coverage.questionCount > 0 && <span className={ui.coverageTopicCount}>{coverage.questionCount}</span>}
      </span>
    );
  };

  const renderAdminTopicPicker = () => (
    <div className={ui.currGenPanel}>
      <div className={ui.currGenHeader}>
        <span className={ui.currGenTitle}>{curriculumTopics.length ? '📚 From Curriculum' : 'Topics from Original Exam'}</span>
        <span className={ui.currGenSub}>All curriculum topics are available. Topics from the original exam are selected by default.</span>
      </div>
      <div>
        <div className={ui.currGenTopicsRow}>
          {topicOptions.map(topic => (
            <button
              key={topic}
              type="button"
              className={`${ui.currGenTopicBtn} ${topics.includes(topic) ? ui.currGenTopicBtnSel : ''}`}
              onClick={() => toggleTopic(topic)}
              aria-pressed={topics.includes(topic)}
            >
              {renderCoverageChip(topic)}
            </button>
          ))}
        </div>
        <div style={{ marginTop: 6, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <button type="button" className={ui.currGenLinkBtn} onClick={() => setTopics(topicOptions)}>Select all</button>
          {topics.length > 0 && <button type="button" className={ui.currGenLinkBtn} onClick={() => setTopics([])}>Clear</button>}
          <span className={ui.currGenSelectedCount}>{topics.length} topic{topics.length === 1 ? '' : 's'} selected</span>
        </div>
        {coverageNoticeTopics.length > 0 && (
          <div className={ui.coverageNotice} role="status">
            {coverageNoticeTopics.length} selected topic{coverageNoticeTopics.length === 1 ? ' already has' : 's already have'} questions generated previously.
          </div>
        )}
      </div>
      {topics.length > 0 && (
        <div className={ui.currGenSelectedTopics} aria-label="Selected topics">
          <span className={ui.currGenSelectedTopicsLabel}>Selected topics:</span>
          {topics.map(topic => (
            <span key={topic} className={ui.currGenSelectedTopic}>
              {topic}
              <button type="button" className={ui.currGenSelectedTopicRemove} onClick={() => toggleTopic(topic)} aria-label={`Remove ${topic}`} title={`Remove ${topic}`}>&times;</button>
            </span>
          ))}
        </div>
      )}
      <div className={ui.currGenControlsRow}>
        <span className={ui.currGenLabel}>Per topic:</span>
        <select className={ui.aiSelect} value={countPerTopic} onChange={event => setCountPerTopic(Number(event.target.value))}>
          {[1, 2, 3, 4, 5].map(count => <option key={count} value={count}>{count}</option>)}
        </select>
        <span className={ui.currGenLabel}>Difficulty:</span>
        <select className={ui.aiSelect} value={difficulty} onChange={event => setDifficulty(event.target.value)}>
          <option value="easy">Easy</option><option value="medium">Medium</option><option value="hard">Hard</option>
        </select>
        <select className={ui.aiSelect} value={questionType} onChange={event => setQuestionType(event.target.value)}>
          <option value="mcq">MCQ</option><option value="theory">Theory</option><option value="mixed">Mixed</option>
        </select>
        <span className={ui.currGenTotal}>{selectedTopics.length * countPerTopic} Qs total</span>
        <button type="button" className={ui.currGenBtn} onClick={generateQuestions} disabled={generating || !selectedTopics.length}>
          {generating ? 'Generating…' : questions.length ? 'Regenerate questions' : 'Generate from Curriculum'}
        </button>
      </div>
    </div>
  );

  const renderTeacherTopicPicker = () => (
    <div className={ui.aiPanel}>
      <div className={ui.aiPanelHeader}>
        <span className={ui.aiPanelTitle}>AI Question Generator</span>
        {curriculumTopics.length > 0 && <span className={ui.aiCurriculumTag}>✓ Curriculum-Grounded</span>}
      </div>
      {topicOptions.length > 0 && (
        <div className={ui.aiTopicsWrap}>
          <div className={ui.aiTopicsLabelRow}>
            <span className={ui.aiTopicsLabel}>From your curriculum:</span>
            {!teacherMultiSelect ? (
              <button className={ui.aiTopicsMultiLink} type="button" onClick={() => setTeacherSelectionMode(true)}>+ Select multiple</button>
            ) : (
              <>
                <button className={ui.aiTopicsMultiLink} type="button" onClick={() => setTeacherSelectionMode(false)}>− Single topic</button>
                {topics.length > 0 && <button className={ui.aiTopicsClearLink} type="button" onClick={() => { setTopics([]); setTopicDraft(''); }}>Clear</button>}
              </>
            )}
          </div>
          <div className={`${ui.aiTopicsChips} ${showAllTopics ? ui.aiTopicsChipsExpanded : ''}`}>
            {(showAllTopics ? topicOptions : topicOptions.slice(0, 5)).map(topic => {
              const active = teacherMultiSelect ? topics.includes(topic) : topicDraft.trim() === topic;
              return (
                <button key={topic} className={`${ui.aiTopicChip} ${active ? ui.aiTopicChipActive : ''}`} onClick={() => selectTeacherTopic(topic)} type="button" aria-pressed={active}>
                  {renderCoverageChip(topic, teacherMultiSelect && active)}
                </button>
              );
            })}
          </div>
          {topicOptions.length > 5 && (
            <button className={ui.aiTopicsMoreBtn} onClick={() => setShowAllTopics(value => !value)} type="button">
              {showAllTopics ? '▲ Show less' : `▼ +${topicOptions.length - 5} more topics`}
            </button>
          )}
          {coverageNoticeTopics.length > 0 && (
            <div className={ui.coverageNotice} role="status">
              {coverageNoticeTopics.length} selected topic{coverageNoticeTopics.length === 1 ? ' already has' : 's already have'} questions generated previously.
            </div>
          )}
        </div>
      )}
      <div className={ui.aiInputRow}>
        {teacherMultiSelect ? (
          <div className={ui.aiMultiTopicInput}>
            {topics.map(topic => (
              <span key={topic} className={ui.aiMultiTopicChip}>
                {topic}
                <button type="button" className={ui.aiMultiTopicRemove} onClick={() => toggleTopic(topic)} aria-label={`Remove ${topic}`}>×</button>
              </span>
            ))}
            {topics.length === 0 && <span className={ui.aiMultiTopicPlaceholder}>Click topics above to add…</span>}
          </div>
        ) : (
          <input className={ui.aiInput} placeholder="Topic (e.g. Photosynthesis)" value={topicDraft} onChange={event => updateTeacherSingleTopic(event.target.value)} />
        )}
        <select className={ui.aiSelect} value={questionType} onChange={event => setQuestionType(event.target.value)}>
          <option value="mcq">MCQ</option><option value="theory">Theory</option><option value="mixed">Mixed</option>
        </select>
        {teacherMultiSelect ? (
          <>
            <span className={ui.aiSelectLabel}>per topic</span>
            <select className={ui.aiSelect} value={countPerTopic} onChange={event => setCountPerTopic(Number(event.target.value))}>
              {[1, 2, 3, 4, 5].map(count => <option key={count} value={count}>{count} Q</option>)}
            </select>
          </>
        ) : (
          <>
            <select className={ui.aiSelect} value={countPerTopic} onChange={event => setCountPerTopic(Number(event.target.value))}>
              {[1, 2, 3, 5, 10].map(count => <option key={count} value={count}>{count} Q{count !== 1 ? 's' : ''}</option>)}
            </select>
            <select className={ui.aiSelect} value={difficulty} onChange={event => setDifficulty(event.target.value)}>
              <option value="easy">Easy</option><option value="medium">Medium</option><option value="hard">Hard</option>
            </select>
          </>
        )}
        <button type="button" className={ui.aiBtn} onClick={generateQuestions} disabled={generating || !selectedTopics.length}>
          {generating ? 'Generating…' : questions.length ? 'Regenerate' : 'Generate'}
        </button>
      </div>
      {teacherMultiSelect && selectedTopics.length > 0 && (
        <div className={ui.aiMultiCount}>
          {selectedTopics.length} topic{selectedTopics.length !== 1 ? 's' : ''} selected · {selectedTopics.length * countPerTopic} questions total
        </div>
      )}
    </div>
  );

  return (
    <div className={ui.modalOverlay} role="presentation" onClick={event => { if (event.target === event.currentTarget) onClose(); }}>
      <section className={`${ui.modalLarge} BubblePopIn`} style={actor === 'admin' ? { maxWidth: 900 } : undefined} role="dialog" aria-modal="true" aria-labelledby="retake-dialog-title" onClick={event => event.stopPropagation()}>
        <header className={ui.modalHeader}>
          <div>
            <div className={ui.modalTitleRow}>
              <h2 id="retake-dialog-title" className={ui.modalTitle}>{result ? 'Assignment results' : 'Allow retake'}</h2>
              <span className={ui.examModeBadge}>Exam retake</span>
            </div>
            <p className={ui.modalSub}>{exam.title} · {selectedStudents.length} student{selectedStudents.length === 1 ? '' : 's'}</p>
          </div>
          <button type="button" className={ui.closeBtn} aria-label="Close" onClick={onClose}>×</button>
        </header>
        {result ? (
          <>
            <div className={ui.modalBody}>
              <p className={successClass}>
                {result.assigned?.length || 0} assigned · {result.skipped?.length || 0} skipped · {result.feeBlocked?.length || 0} blocked by fees
              </p>
              {result.skipped?.length > 0 && <p className={helperClass}>Some students were skipped because their attempt is not eligible or changed before assignment.</p>}
              {result.feeBlocked?.length > 0 && <p className={helperClass}>Assigned, but blocked from starting until outstanding fees are settled.</p>}
            </div>
            <div className={ui.modalFooter}>
              <button type="button" className={ui.saveBtn} onClick={onClose}>Done</button>
            </div>
          </>
        ) : (
          <>
            <div className={ui.modalBody}>
              <p className={helperClass}>
                <strong>{selectedStudents.length} selected:</strong>{' '}
                {selectedStudents.slice(0, 8).map(student => `${student.firstName} ${student.lastName}`.trim()).join(', ')}
                {selectedStudents.length > 8 ? ` and ${selectedStudents.length - 8} more` : ''}
              </p>
              {loadingSource && <p className={helperClass}>Loading exam topics and settings…</p>}
              <div className={fieldClass}>
                <label>Retake questions</label>
                <div style={{ display: 'flex', gap: 14, marginTop: 6, flexWrap: 'wrap' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', fontSize: '0.82rem' }}>
                    <input className="schoolRadio" type="radio" name="retakeQuestionMode" checked={questionSetMode === 'generated'} onChange={() => setQuestionSetMode('generated')} style={{ margin: 0, flexShrink: 0 }} />
                    <span style={{ lineHeight: 1.4 }}>Generate fresh questions</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', fontSize: '0.82rem' }}>
                    <input className="schoolRadio" type="radio" name="retakeQuestionMode" checked={questionSetMode === 'reuse'} onChange={() => setQuestionSetMode('reuse')} style={{ margin: 0, flexShrink: 0 }} />
                    <span style={{ lineHeight: 1.4 }}>Reuse the original questions (skip generation)</span>
                  </label>
                </div>
              </div>
              {questionSetMode === 'generated' && !loadingSource && (
                <>
                  {actor === 'admin' ? renderAdminTopicPicker() : renderTeacherTopicPicker()}
                  {!topicOptions.length && <p className={helperClass}>No curriculum or original-exam topics were found. You can still add questions manually below.</p>}
                  {error && <div className={errorClass} role="alert">{error}</div>}
                  <div className={fieldClass}>
                    <label>Total score *</label>
                    <input className={ui.formInput} type="number" min="0.01" step="0.01" value={totalScore} onChange={event => setTotalScore(event.target.value)} />
                  </div>
                  <div className={ui.equalMarksBar}>
                    <label className={toggleSwitchClass}>
                      <input className="schoolCheckbox" type="checkbox" checked={equalMarks} onChange={event => setEqualMarks(event.target.checked)} />
                      <span className={toggleSliderClass} />
                    </label>
                    <div>
                      <div className={ui.equalMarksLabel}>Equal marks per question</div>
                      <div className={ui.equalMarksSub}>
                        {equalMarks
                          ? `Each question: ${questions.length > 0 && Number(totalScore) > 0 ? (Number(totalScore) / questions.length).toFixed(2) : '—'} marks`
                          : 'Set individual marks per question below'}
                      </div>
                    </div>
                    {!equalMarks && (
                      <span className={ui.scoreSumBadge}>
                        {normalizedQuestions.reduce((sum, question) => sum + question.score, 0).toFixed(2)} / {Number(totalScore) || 0} marks
                      </span>
                    )}
                  </div>
                  {renderQuestionEditor?.({
                    questions,
                    onChange: updateQuestion,
                    onRemove: removeQuestion,
                    onAdd: addBlankQuestion,
                    equalMarks,
                    autoScore: questions.length ? (Number(totalScore) || 0) / questions.length : 0,
                    uploadButton: (
                      <button type="button" className={ui.addQBtn || styles.uploadButton} onClick={openUploadQuestions}>
                        <UploadFileIcon fontSize="small" /> Upload questions
                      </button>
                    ),
                  })}
                </>
              )}
              {questionSetMode === 'reuse' && <p className={helperClass}>The same questions will be assigned again. Select this only when the student should retry the original set.</p>}
              <div className={styles.dateFieldsRow}>
                <div className={fieldClass}>
                  <label>Retake start date *</label>
                  {DateTimePickerComponent ? (
                    <DateTimePickerComponent
                      value={startDate}
                      min={localDateTimeValue(initialWindow.minStart)}
                      placeholder="Pick retake start date"
                      onChange={updateStartDate}
                    />
                  ) : (
                    <input
                      className={ui.formInput}
                      type="datetime-local"
                      value={startDate}
                      min={localDateTimeValue(initialWindow.minStart)}
                      onChange={updateStartDate}
                      required
                    />
                  )}
                </div>
                <div className={fieldClass}>
                  <label>Retake end date *</label>
                  {DateTimePickerComponent ? (
                    <DateTimePickerComponent
                      value={endDate}
                      min={localDateTimeValue(minEndDate)}
                      placeholder="Pick retake end date"
                      onChange={event => setEndDate(event.target.value)}
                    />
                  ) : (
                    <input
                      className={ui.formInput}
                      type="datetime-local"
                      value={endDate}
                      min={localDateTimeValue(minEndDate)}
                      onChange={event => setEndDate(event.target.value)}
                      required
                    />
                  )}
                  <span className={helperClass}>The window must last at least 1 hour and close within 60 days.</span>
                </div>
              </div>
              <div className={fieldClass}>
                <label>Reason *</label>
                <textarea className={ui.formTextarea} value={reason} onChange={event => setReason(event.target.value)} minLength={5} maxLength={500} rows={3} required aria-required="true" placeholder="Record why these students are being offered another sitting." />
              </div>
              {reportCardReleased && <p className={helperClass}>This term's report card has already been released. A completed retake will update the score used on future report-card views.</p>}
              <div className={styles.warning} role="note">
                <p>Outstanding-fee restrictions still apply. A granted retake does not bypass the school fee gate.</p>
                <p>The new graded score will replace the current score, even if it is lower. The previous result remains in staff history if the retake is cancelled or expires.</p>
              </div>
              {error && questionSetMode !== 'generated' && <div className={errorClass} role="alert">{error}</div>}
            </div>
            <div className={ui.modalFooter}>
              <button type="button" className={ui.cancelBtn} onClick={onClose} disabled={saving}>Cancel</button>
              <button type="button" className={ui.publishBtn} onClick={submit} disabled={saving || loadingSource || !selectedStudents.length || (questionSetMode === 'generated' && !questions.length)}>
                {saving ? 'Assigning…' : 'Assign retake'}
              </button>
            </div>
          </>
        )}
      </section>
      {uploadQModal && (
        <div
          className={ui.modalOverlay}
          style={{ zIndex: 1600 }}
          onClick={event => { if (!uploadQLoading && event.target === event.currentTarget) closeUploadQuestions(); }}
        >
          <div className={`${ui.modal} BubblePopIn`} style={{ maxWidth: 460 }} onClick={event => event.stopPropagation()}>
            <div className={ui.modalHeader}>
              <h2 className={ui.modalTitle}>Upload Questions from File</h2>
              <button type="button" className={ui.closeBtn} onClick={closeUploadQuestions} disabled={uploadQLoading}>✕</button>
            </div>
            <p style={{ fontSize: '0.82rem', color: '#607d8b', margin: '0 0 14px' }}>
              Upload a PDF, Word document, spreadsheet, image, or plain text file containing questions.
              Text PDFs and structured spreadsheets are read directly; scans and images use AI vision.
            </p>
            <div
              style={{ border: '2px dashed #b2d8da', borderRadius: 10, padding: '22px 16px', textAlign: 'center', cursor: 'pointer', background: '#f0fafb', marginBottom: 14 }}
              onClick={() => !uploadQLoading && uploadQFileRef.current?.click()}
              onDragOver={event => event.preventDefault()}
              onDrop={event => {
                event.preventDefault();
                const file = event.dataTransfer.files[0];
                if (!uploadQLoading && file) {
                  uploadQFileDataRef.current = file;
                  setUploadQFileName(file.name);
                }
              }}
            >
              <input
                ref={uploadQFileRef}
                type="file"
                accept=".pdf,.txt,.docx,.doc,.xlsx,.xls,.csv,.png,.jpg,.jpeg,.webp,.gif,.bmp,.tif,.tiff"
                style={{ display: 'none' }}
                onChange={event => {
                  const file = event.target.files[0];
                  if (file) {
                    uploadQFileDataRef.current = file;
                    setUploadQFileName(file.name);
                  }
                }}
              />
              <UploadFileIcon style={{ fontSize: '2rem', color: '#5fbec5', marginBottom: 6 }} />
              {uploadQFileName
                ? <div style={{ fontWeight: 700, color: '#0c7c84', fontSize: '0.88rem' }}>{uploadQFileName}</div>
                : <><div style={{ fontWeight: 600, color: '#607d8b', fontSize: '0.88rem' }}>Click or drag &amp; drop</div>
                  <div style={{ fontSize: '0.75rem', color: '#90a4ae' }}>PDF, Word, Excel, CSV, image, or TXT - max 10 MB</div></>}
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: '0.82rem', fontWeight: 600, color: '#546e7a', display: 'block', marginBottom: 5 }}>Extract as</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {['mcq', 'theory'].map(type => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setUploadQType(type)}
                    disabled={uploadQLoading}
                    style={{ padding: '6px 18px', borderRadius: 8, border: '1.5px solid', borderColor: uploadQType === type ? '#5fbec5' : '#dde4e8', background: uploadQType === type ? 'rgba(95,190,197,0.12)' : '#fff', color: uploadQType === type ? '#0c7c84' : '#607d8b', fontWeight: uploadQType === type ? 700 : 500, fontSize: '0.84rem', cursor: 'pointer' }}
                  >
                    {type === 'mcq' ? 'MCQ' : 'Theory'}
                  </button>
                ))}
              </div>
            </div>
            {uploadQError && <div className={errorClass} style={{ marginBottom: 10 }} role="alert">{uploadQError}</div>}
            <div className={ui.modalFooter}>
              <button type="button" className={ui.cancelBtn} onClick={closeUploadQuestions} disabled={uploadQLoading}>Cancel</button>
              <button type="button" className={ui.saveBtn} onClick={handleUploadQuestions} disabled={uploadQLoading || !uploadQFileName}>
                {uploadQLoading ? <><span className={ui.aiSpinner} /> Extracting…</> : 'Extract Questions'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
